// import Dexie, { type EntityTable } from 'dexie';
import Dexie, { EntityTable } from 'dexie';
import { PhrasesData, PhrasesRootData } from 'services/api/phrase';
import type { ConversationFilterAdvance } from 'hooks/business/conversationsHooks/useFilterAdvance';
import type { StoreSelectorValue } from 'components/StoreSelector/interface';

/**
 * 会话配置
 */
export interface ChatPreference {
  conversationId: string;
  /**
   * 翻译语言设置
   */
  buyerLang?: General.Language;
  grantedAssignTips?: number;
}

/**
 * api 缓存 tag
 */
export interface ApiCacheTag {
  apiKey: string;
  etag: string;
}

/**
 * 表情包数据
 */
export interface Sticker {
  id: string;
  channel: General.PlatformUpperCase;
  enable: boolean;
  name?: Conversation.StickerName;
  ossStickerAddress: string;
  pid: string;
  region: General.UpperCaseRegion | 'GLOBAL';
  sid: string;
}

/**
 * 最近使用的表情包
 */
export interface RecentUsedSticker {
  id: string;
  used: number;
}

export interface Phrases extends PhrasesData {
  /**
   * 是否关闭联想
   */
  disabledRelate: boolean;
}

export interface QAPairCache {
  conversationId: string;
  cancelCount: number;
}

/**
 * 用户偏好设置
 */
export interface Preference {
  key: string;
  value: string;
}

/** 已提交AI回复有误的消息标记 */
export interface RepliedQAPairMsgMark {
  conversationId: string;
  msgId: string;
}

export interface TodoTypeSource {
  taskId: number;
  type: Todo.Type;
  localEnable: boolean;
  sortIndex?: number;
}

/**
 * 分类筛选状态 (Mine/All)
 */
export interface CategoryFilterState {
  category: 'mine' | 'all';
  filterAdvance?: ConversationFilterAdvance;
  filterAdvanceMode?: 'single' | 'multiple';
  sort?: 'asc' | 'desc' | 'waitAsc' | 'waitDesc';
  todoFilter?: 'all' | 'unprocessed';
  storeFilter?: StoreSelectorValue;
}

export type IUserDB = Dexie & {
  chatPreference: EntityTable<ChatPreference, 'conversationId'>;
  apiCacheTag: EntityTable<ApiCacheTag, 'apiKey'>;
  sticker: EntityTable<Sticker, 'id'>;
  recentUsedSticker: EntityTable<RecentUsedSticker, 'id'>;
  phraseGroup: EntityTable<Phrase.Group, 'id'>;
  phrasesRoot: EntityTable<Omit<PhrasesRootData, 'phraseMap'>, 'id'>;
  phrases: EntityTable<Phrases, 'id'>;
  qaPair: EntityTable<QAPairCache, 'conversationId'>;
  preference: EntityTable<Preference, 'key'>;
  repliedQAPairMsgMark: EntityTable<RepliedQAPairMsgMark, 'conversationId'>;
  todoTypeSource: EntityTable<TodoTypeSource, 'taskId'>;
  categoryFilterState: EntityTable<CategoryFilterState, 'category'>;
};

export class UserDB {
  static _db: IUserDB | undefined;

  /**
   * 初始化登录用户数据库(indexDB)
   */
  static init(userId: string) {
    if (UserDB._db) return UserDB._db;

    console.log('init db ', userId);
    UserDB._db = new Dexie(`fett_${userId}`, { cache: 'disabled' }) as IUserDB;

    UserDB._db.version(1).stores({
      chatPreference: '&conversationId, buyerLang, grantedAssignTips',
      apiCacheTag: '&apiKey, etag',
      sticker:
        '&id, [channel+region+pid+sid], [channel+region+sid], [channel+region], channel, enable, name, ossStickerAddress, pid, region, sid',
      recentUsedSticker: '&id, used',
      phraseGroup:
        '&id, [channel+groupType], applyScope, channel, enable, groupType, name, orgId, phrase, phraseRootNum, region, sortNumber, userId',
      phrasesRoot:
        '&id, [id+channel], channel, groupName, groupType, phraseGroupId, shortcutKey, sortNumber, tags',
      phrases:
        '&id, content, images, phraseElems, phraseGroupId, phraseRootId, phraseType, region, resources, translationContent, disabledRelate',
      todoTypeSource: '&taskId, type, localEnable',
    });

    UserDB._db.version(2).stores({
      qaPair: '&conversationId, cancelCount',
    });

    UserDB._db.version(3).stores({
      preference: '&key, value',
    });

    UserDB._db.version(4).stores({
      repliedQAPairMsgMark: 'msgId, conversationId',
    });

    // version 5: 删除旧表（主键 &taskId 无法直接改为复合主键）
    UserDB._db.version(5).stores({
      todoTypeSource: null,
    });

    // version 6: 重建表（复合主键 [taskId+type]）
    UserDB._db.version(6).stores({
      todoTypeSource: '[taskId+type], localEnable, sortIndex',
    });

    // version 7: 添加分类筛选状态表 (Mine/All)
    UserDB._db.version(7).stores({
      categoryFilterState: '&category, filterAdvance, filterAdvanceMode, sort, todoFilter',
    });

    // version 8: 分类筛选状态表新增店铺筛选字段
    UserDB._db.version(8).stores({
      categoryFilterState:
        '&category, filterAdvance, filterAdvanceMode, sort, todoFilter, storeFilter',
    });

    UserDB._db.open().catch((err) => {
      console.error('init db fail. open error', err);
    });

    return UserDB._db;
  }

  /**
   * 断开 DB 连接
   */
  static destroy() {
    if (UserDB._db) {
      console.log('close db');
      UserDB._db.close();
      UserDB._db = undefined;
    }
  }

  static get instance() {
    if (!UserDB._db) {
      console.warn('not init db');
    }
    return UserDB._db;
  }
}
